import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"

export class ProductImportProgressController extends Controller {
    static values = { url: String }

    connect() {
        this.stopped = false
        this.failures = 0
        this.filterValue = "all"
        this.dirty = false
        this.visibility = () => { this.schedule(0) }
        // Frame history can cache a snapshot while this controller stays mounted.
        // Actual removal is handled by Stimulus disconnect().
        this.beforeCache = () => this.dialog?.close()
        this.beforeUnload = event => { if (this.dirty || this.detailDirty) { event.preventDefault(); event.returnValue = "" } }
        this.navigation = event => {
            const link = event.target.closest("a")
            if (!link || event.defaultPrevented || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey
                || (link.target && link.target !== "_self") || link.dataset.action?.includes("openDetails") || !(this.dirty || this.detailDirty)) return
            if (!window.confirm("Leave this view and discard unsaved changes?")) event.preventDefault()
        }
        this.dialog = this.element.querySelector("#import-entry-dialog")
        this.frame = this.element.closest("turbo-frame")
        this.failedFrame = event => this.frameFailed(event)
        this.frame?.addEventListener("turbo:frame-missing", this.failedFrame)
        this.frame?.addEventListener("turbo:fetch-request-error", this.failedFrame)
        this.dialogClosed = () => {
            this.detailDirty = false
            this.detailRequest?.abort()
            this.detailSequence = (this.detailSequence || 0) + 1
            const opener = this.detailEntry ? this.element.querySelector(`#${this.detailEntry} a`) : this.detailOpener
            opener?.focus({ preventScroll: true })
        }
        this.dialog?.addEventListener("close", this.dialogClosed)
        this.dialogCancel = event => { if (!this.confirmCloseDetails()) event.preventDefault() }
        this.dialog?.addEventListener("cancel", this.dialogCancel)
        this.backdropPressed = false
        this.dialogPointerDown = event => { this.backdropPressed = event.button === 0 && this.isBackdrop(event) }
        this.dialogClick = event => {
            if (this.backdropPressed && this.isBackdrop(event)) this.closeDetails()
            this.backdropPressed = false
        }
        this.dialog?.addEventListener("pointerdown", this.dialogPointerDown)
        this.dialog?.addEventListener("click", this.dialogClick)
        document.addEventListener("visibilitychange", this.visibility)
        document.addEventListener("turbo:before-cache", this.beforeCache)
        window.addEventListener("beforeunload", this.beforeUnload)
        this.element.addEventListener("click", this.navigation)
        const saved = new URL(window.location.href).searchParams.get("saved")
        this.updateControls()
        this.schedule(saved === "retry" ? 0 : 5000)
    }

    disconnect() {
        this.stopped = true
        clearTimeout(this.timer)
        this.request?.abort()
        this.detailRequest?.abort()
        this.cancelRender?.()
        this.dialog?.removeEventListener("close", this.dialogClosed)
        this.dialog?.removeEventListener("cancel", this.dialogCancel)
        this.editRequest?.abort()
        this.dialog?.removeEventListener("pointerdown", this.dialogPointerDown)
        this.dialog?.removeEventListener("click", this.dialogClick)
        this.frame?.removeEventListener("turbo:frame-missing", this.failedFrame)
        this.frame?.removeEventListener("turbo:fetch-request-error", this.failedFrame)
        if (this.dialog?.open) this.dialog.close()
        document.removeEventListener("visibilitychange", this.visibility)
        document.removeEventListener("turbo:before-cache", this.beforeCache)
        window.removeEventListener("beforeunload", this.beforeUnload)
        this.element.removeEventListener("click", this.navigation)
    }

    state() { return this.element.querySelector("#import-update-state") }
    rows() { return [...this.element.querySelectorAll("[data-proposal-group]")] }
    checkboxes() { return [...this.element.querySelectorAll('input[name="entry"]')] }
    notice(text, form = false) {
        const node = this.element.querySelector(form ? "#import-form-notice" : "#import-update-notice")
        if (node) node.textContent = text
    }

    changed(event) {
        if (event.target.closest("#import-source-rows")) this.dirty = true
        this.updateControls()
    }

    submitting(event) {
        this.sending = true
        clearTimeout(this.timer)
        this.request?.abort()
        this.notice(event.target.closest("#import-source-rows") ? "Saving selections…" : "Submitting…", true)
    }

    submitted(event) {
        this.sending = false
        if (event.detail.success) { this.dirty = false; this.notice("", true) }
        else if (!event.detail.fetchResponse) this.notice("Unable to submit. Your changes are still here; please try again.", true)
        this.schedule()
    }

    frameFailed(event) {
        event.preventDefault()
        this.sending = false
        this.notice("Unable to load this view. Your current content is still here; retry the link or refresh after signing in.")
        this.schedule()
    }

    schedule(delay = 5000) {
        clearTimeout(this.timer)
        if (!this.stopped && !document.hidden && !this.authExpired)
            this.timer = setTimeout(() => this.refresh(), delay)
    }

    async refresh() {
        if (this.stopped || this.busy || this.sending || document.hidden || this.authExpired) return
        if (this.state()?.dataset.active !== "true") return
        this.busy = true
        this.request = new AbortController()
        const timeout = setTimeout(() => this.request?.abort(), 15000)
        try {
            const url = new URL(this.urlValue, window.location.href)
            url.searchParams.set("version", this.state()?.dataset.version || "")
            const response = await fetch(url, { credentials: "same-origin", cache: "no-store", signal: this.request.signal,
                headers: { Accept: "text/vnd.turbo-stream.html" } })
            if (response.redirected || response.status === 401 || response.status === 403) {
                this.authExpired = true
                this.notice("Updates paused. Sign in again, then refresh this page.")
                return
            }
            if (!response.ok || !response.headers.get("Content-Type")?.startsWith("text/vnd.turbo-stream.html"))
                throw new Error("Unexpected update response")
            const html = await response.text()
            if (this.stopped || this.sending || document.hidden) return
            await this.applyStreams(html)
            this.failures = 0
            this.notice("")
        } catch {
            if (!this.stopped && !this.sending) {
                this.failures++
                this.notice("Updates temporarily unavailable. Your changes are preserved; retrying shortly.")
            }
        } finally {
            clearTimeout(timeout)
            this.busy = false
            this.schedule(Math.min(60000, 5000 * (2 ** this.failures)))
        }
    }

    async applyStreams(html) {
        if (!html.trim()) return
        const parsed = new DOMParser().parseFromString(html, "text/html")
        const incoming = [...parsed.querySelectorAll("turbo-stream")]
        if (!incoming.length) throw new Error("Missing update streams")
        const selected = this.checkboxes().filter(box => box.checked).map(box => box.value)
        const focused = document.activeElement?.name === "entry" ? document.activeElement.value : null
        const source = this.element.querySelector("#import-source-panel")
        const initialRevision = this.state()?.dataset.revision
        const openDetails = [...this.element.querySelectorAll("details[open]")].map(node => {
            const container = node.closest("[id]")
            return { id: container?.id, index: container ? [...container.querySelectorAll("details")].indexOf(node) : -1 }
        })
        const anchor = [...this.element.querySelectorAll("tr[id], [id^='import-catalog-'], #import-proposals")]
            .find(node => node.getBoundingClientRect().bottom > 0 && !node.closest("[hidden]"))
        const top = anchor?.getBoundingClientRect().top
        const scroll = window.scrollY
        const streams = []
        for (const stream of incoming) {
            const action = stream.getAttribute("action")
            const target = stream.getAttribute("target")
            const replacement = stream.querySelector("template")?.content.firstElementChild
            if (!replacement) throw new Error("Missing stream content")
            if (action === "append" && target === "import-proposal-rows" && /^import-entry-\d+$/.test(replacement.id)) {
                if (!this.element.querySelector("#import-proposal-rows")) continue
                const current = this.element.querySelector(`#${replacement.id}`)
                if (current?.dataset.importVersion === replacement.dataset.importVersion) continue
                if (current && Number(replacement.dataset.entryVersion) < Number(current.dataset.entryVersion)) continue
                if (current) { stream.setAttribute("action", "replace"); stream.setAttribute("target", replacement.id) }
                if (this.dialog?.open && this.detailEntry === replacement.id) this.detailChanged = true
            } else if (action === "replace" && (["import-overview-live", "import-restickering-live", "import-source-panel", "import-activity-live", "import-update-state"].includes(target) || /^import-catalog-\d+$/.test(target) || /^import-entry-content-\d+$/.test(target))) {
                const current = this.element.querySelector(`#${target}`)
                if (!current || replacement.id !== target) continue
                if (/^import-entry-content-\d+$/.test(target) && Number(replacement.dataset.detailRevision) < Number(current.dataset.detailRevision)) continue
                if (target === "import-source-panel" && (source?.dataset.sourceEditable === "true" || this.dirty)) continue
                if (current.dataset.importVersion && current.dataset.importVersion === replacement.dataset.importVersion) continue
            } else throw new Error("Unexpected update target")
            streams.push(stream)
        }
        await this.renderStreams(streams)
        if (this.stopped) return
        for (const box of this.checkboxes()) box.checked = selected.includes(box.value) && !box.disabled
        for (const { id, index } of openDetails) if (id && index >= 0)
            this.element.querySelector(`#${id}`)?.querySelectorAll("details")[index]?.setAttribute("open", "")
        const selection = this.element.querySelector('[data-controller~="product-import-selection"]')
        if (selection) this.application.getControllerForElementAndIdentifier(selection, "product-import-selection")?.refreshWarnings()
        if (focused) this.checkboxes().find(box => box.value === focused)?.focus({ preventScroll: true })
        this.updateControls()
        if (source?.dataset.sourceEditable === "true" && initialRevision !== this.state()?.dataset.revision) {
            this.notice("Source selections changed elsewhere. Reload the Source selection tab before saving.", true)
        }
        if (anchor?.id) {
            const current = this.element.querySelector(`#${anchor.id}`)
            if (current) window.scrollBy(0, current.getBoundingClientRect().top - top)
            else window.scrollTo(0, scroll)
        }
        if (this.detailChanged && this.dialog?.open) {
            if (!this.detailEditing() && !this.savingEntry && this.detailUrl) {
                await this.loadDetails(this.detailUrl, { background: true })
                return
            }
            const notice = this.element.querySelector("#import-detail-notice")
            notice.replaceChildren(document.createTextNode("This entry has changed. "))
            const button = document.createElement("button")
            button.type = "button"
            button.className = "underline cursor-pointer text-tertiary"
            button.textContent = "Refresh details"
            button.addEventListener("click", () => this.loadDetails(this.detailUrl))
            notice.append(button)
        }
    }

    async renderStreams(streams) {
        if (!streams.length) return
        const token = String((this.renderSequence || 0) + 1)
        this.renderSequence = Number(token)
        for (const stream of streams) stream.dataset.importRender = token
        await new Promise((resolve, reject) => {
            let remaining = streams.length
            const cleanup = () => { clearTimeout(timeout); document.removeEventListener("turbo:before-stream-render", listener); this.cancelRender = null }
            const finish = () => { cleanup(); resolve() }
            const listener = event => {
                if (event.target.dataset.importRender !== token) return
                const render = event.detail.render
                event.detail.render = async element => {
                    try {
                        if (!this.stopped && !this.sending) await render(element)
                        if (--remaining === 0) finish()
                    } catch (error) { cleanup(); reject(error) }
                }
            }
            const timeout = setTimeout(() => { cleanup(); reject(new Error("Update render timeout")) }, 10000)
            this.cancelRender = finish
            document.addEventListener("turbo:before-stream-render", listener)
            Turbo.renderStreamMessage(streams.map(stream => stream.outerHTML).join(""))
        })
    }

    filter(event) { this.filterValue = event.currentTarget.dataset.proposalFilter; this.updateControls() }
    selectReady() { for (const box of this.checkboxes()) if (!box.closest("tr").hidden && !box.disabled) box.checked = true; this.updateControls() }
    clearSelection() { for (const box of this.checkboxes()) box.checked = false; this.updateControls() }
    updateControls() {
        const choices = [...this.element.querySelectorAll('[data-disposition-control] select')]
        const sourceSummary = this.element.querySelector('[data-source-summary]')
        if (sourceSummary && choices.length) {
            const included = choices.filter(choice => choice.value !== "Excluded")
            const units = included.reduce((sum, choice) => sum + Number(choice.parentElement.dataset.units), 0)
            sourceSummary.textContent = `${included.length} included · ${choices.length - included.length} excluded · ${units} included units`
        }
        for (const row of this.rows()) row.hidden = this.filterValue !== "all" && row.dataset.proposalGroup !== this.filterValue
        for (const button of this.element.querySelectorAll("[data-proposal-filter]")) {
            const filter = button.dataset.proposalFilter
            const active = filter === this.filterValue
            button.setAttribute("aria-pressed", String(active))
            button.classList.toggle("button--primary", active)
            button.classList.toggle("button--secondary", !active)
            button.querySelector("[data-filter-count]").textContent = this.rows().filter(row => filter === "all" || row.dataset.proposalGroup === filter).length
        }
        const chosen = this.checkboxes().filter(box => box.checked)
        const hidden = chosen.filter(box => box.closest("tr").hidden).length
        const units = chosen.reduce((sum, box) => sum + Number(box.dataset.units), 0)
        const summary = this.element.querySelector("[data-approval-summary]")
        if (summary) summary.textContent = chosen.length ? `${chosen.length} selected · ${units} units${hidden ? ` · ${hidden} hidden by filter` : ""}` : "Select ready entries to approve."
        const submit = this.element.querySelector("[data-approval-submit]")
        if (submit && !this.sending) submit.disabled = !chosen.length
        const empty = this.element.querySelector("#import-proposals-empty")
        if (empty) {
            empty.hidden = this.rows().some(row => !row.hidden)
            empty.textContent = this.rows().length ? "No entries match this filter." : "Proposals will appear here as preparation progresses."
        }
    }

    openDetails(event) {
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return
        event.preventDefault()
        this.detailOpener = event.currentTarget
        this.detailEntry = event.currentTarget.closest("tr").id
        this.dialog.showModal()
        this.loadDetails(event.currentTarget.href)
    }
    entryChanged() { this.detailDirty = true }
    detailEditing() {
        return this.detailDirty || !!this.element.querySelector('#import-entry-detail-body details[data-entry-editor][open]')
    }
    cancelEntryEdit(event) {
        event.preventDefault()
        const editor = event.currentTarget.closest("details")
        editor.querySelector("form")?.reset()
        editor.open = false
        this.detailDirty = false
        if (this.detailUrl) this.loadDetails(this.detailUrl)
    }
    confirmCloseDetails() {
        return !this.detailDirty || window.confirm("Discard unsaved entry changes?")
    }
    async saveEntry(event) {
        event.preventDefault()
        const form = event.currentTarget
        if (this.savingEntry || (!event.submitter?.formNoValidate && !form.reportValidity())) return
        const entry = form.dataset.entryId
        const data = new FormData(form, event.submitter)
        const buttons = [...form.querySelectorAll("button")]
        this.savingEntry = true
        this.sending = true
        clearTimeout(this.timer)
        this.request?.abort()
        this.detailRequest?.abort()
        this.detailSequence = (this.detailSequence || 0) + 1
        for (const button of buttons) button.disabled = true
        const controller = this.editRequest = new AbortController()
        const timeout = setTimeout(() => controller.abort(), 15000)
        const notice = () => this.dialog?.open && this.detailEntry === entry ? this.element.querySelector("#import-detail-notice") : null
        if (notice()) notice().textContent = "Saving changes…"
        try {
            const response = await fetch(form.action, { method: "POST", body: data, credentials: "same-origin", cache: "no-store",
                signal: controller.signal, headers: { Accept: "text/vnd.turbo-stream.html" } })
            if (response.redirected || ![200, 422].includes(response.status) || !response.headers.get("Content-Type")?.startsWith("text/vnd.turbo-stream.html"))
                throw new Error("Save unavailable")
            const html = await response.text()
            if (this.stopped) return
            this.sending = false
            await this.applyStreams(html)
            if (this.dialog?.open && this.detailEntry === entry) {
                this.detailDirty = response.status !== 200
                this.detailChanged = false
                if (notice()) notice().textContent = response.status === 200 ? "Changes saved. Review the updated proposal before approving." : "Changes were not saved. Review the errors below."
            }
        } catch {
            if (!this.stopped && notice()) notice().textContent = "Unable to confirm this save. Your changes are still here; refresh details to check the current revision before retrying."
        } finally {
            clearTimeout(timeout)
            for (const button of buttons) button.disabled = false
            this.savingEntry = false
            this.sending = false
            this.schedule()
        }
    }
    isBackdrop(event) {
        if (event.target !== this.dialog) return false
        const bounds = this.dialog.getBoundingClientRect()
        return event.clientX < bounds.left || event.clientX >= bounds.right
            || event.clientY < bounds.top || event.clientY >= bounds.bottom
    }
    closeDetails() { if (this.confirmCloseDetails()) this.dialog.close() }
    async loadDetails(url, { background = false } = {}) {
        if (background && (this.detailEditing() || this.savingEntry)) return
        if (!background && this.detailDirty && !window.confirm("Discard unsaved entry changes and refresh details?")) return
        if (!background) this.detailDirty = false
        this.detailUrl = url
        const sequence = this.detailSequence = (this.detailSequence || 0) + 1
        this.detailRequest?.abort()
        const controller = new AbortController()
        this.detailRequest = controller
        const timeout = setTimeout(() => controller.abort(), 15000)
        const body = this.element.querySelector("#import-entry-detail-body")
        const scroll = this.dialog?.scrollTop
        if (!background) {
            body.textContent = "Loading entry details…"
            this.element.querySelector("#import-detail-notice").textContent = ""
        }
        try {
            const response = await fetch(url, { credentials: "same-origin", cache: "no-store", signal: controller.signal, headers: { "Turbo-Frame": "import-entry-details" } })
            if (!response.ok || response.redirected) throw new Error("Details unavailable")
            const parsed = new DOMParser().parseFromString(await response.text(), "text/html")
            const frame = parsed.querySelector('turbo-frame[id="import-entry-details"]')
            if (!frame) throw new Error("Missing details")
            if (this.stopped || sequence !== this.detailSequence || !this.dialog.open) return
            if (background && (this.detailEditing() || this.savingEntry)) return
            body.replaceChildren(...frame.childNodes)
            this.detailChanged = false
            if (background) {
                this.dialog.scrollTop = scroll
                this.element.querySelector("#import-detail-notice").textContent = "Entry details updated."
            }
        } catch {
            if (!this.stopped && sequence === this.detailSequence && this.dialog.open) {
                const target = background ? this.element.querySelector("#import-detail-notice") : body
                target.textContent = background ? "Unable to refresh entry details. Current details are preserved. " : "Unable to load entry details. "
                const retry = document.createElement("button")
                retry.type = "button"; retry.className = "underline cursor-pointer"; retry.textContent = "Retry"
                retry.addEventListener("click", () => this.loadDetails(url, { background })); target.append(retry)
            }
        } finally { clearTimeout(timeout) }
    }
}
